import { forwardRef } from 'react';

import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import {
  DataTableFiltersV2,
  TableFilterConfig,
  UseTableFiltersReturn,
} from '@/components/data-table/filters-v2';
import {
  DateRangePickerV2,
  DateRangePreset,
  DateRangeV2,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';

import { TIME_OFFS_TABLE_ID } from '../../constants/time-offs-filter-keys';
import { TimeOffRequest } from '../../types/time-off-request';

interface TimeOffsToolbarProps {
  /** Sticky top offset (px) — pins right below the collapsed balances chip strip. */
  topPx: number;
  filtersConfig: TableFilterConfig[];
  filterState: UseTableFiltersReturn;
  dates: DateRangeV2 | null;
  selectedPreset: DateRangePreset | null;
  onDateChange: (next: DateRangeV2) => void;
  onPresetSelect: (preset: DateRangePreset) => void;
  onDateReset: () => void;
  table: Table<TimeOffRequest>;
}

/**
 * The "Requests & History" title + filters + columns row. Sticky-pinned just
 * below the collapsing balances toolbar. Forwards a ref so the page can
 * measure its height and pin the table column header exactly below it.
 */
export const TimeOffsToolbar = forwardRef<HTMLDivElement, TimeOffsToolbarProps>(
  (
    {
      topPx,
      filtersConfig,
      filterState,
      dates,
      selectedPreset,
      onDateChange,
      onPresetSelect,
      onDateReset,
      table,
    },
    ref
  ) => {
    const { t } = useTranslation('authenticated.timeOff');

    return (
      <div
        ref={ref}
        className="sticky z-20 -mx-4 flex flex-col gap-3 bg-background px-4 pb-4 pt-3"
        style={{ top: topPx }}
      >
        <h2 className="text-lg font-semibold">{t('table.title')}</h2>
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <DataTableFiltersV2
              filtersConfig={filtersConfig}
              filterState={filterState}
              tableId={TIME_OFFS_TABLE_ID}
              showSaveButton={false}
            />
            <DateRangePickerV2
              dates={dates}
              onChange={onDateChange}
              onPresetSelect={onPresetSelect}
              selectedPreset={selectedPreset}
              onReset={onDateReset}
            />
          </div>
          <DataTableColumnsSelector table={table} />
        </div>
      </div>
    );
  }
);

TimeOffsToolbar.displayName = 'TimeOffsToolbar';
